import React, { useState, useEffect } from "react";
import { Card, Container } from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { PaginationComponent } from "../../casp/common/PaginationComponent";
import Button from "@mui/material/Button";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import { withTranslation } from "react-i18next";
import { getSuccessStories, deleteSuccessStory } from "./successStoriesApi";
import AddSuccessStoryDrawer from "./AddSuccessStoryDrawer";
import UserProfile from "../../casp/common/userProfile";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import ModeEditOutlineOutlinedIcon from "@mui/icons-material/ModeEditOutlineOutlined";
import Loader from "../../../Components/Common/Loader";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import NodataFound from "../../Common/NoDataFound";
import MilestoneImage from "../../../assets/images/icons/achieve.png";

const SuccessStoriesTab = ({
  projectId,
  disabled,
  t,
  refresh: initialRefresh,
}) => {
  const [successStories, setSuccessStories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedStory, setSelectedStory] = useState(null);
  const [refresh, setRefresh] = useState(initialRefresh || false);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [paginationObject, setPaginationObject] = useState({
    total: 0,
    per_page: 10,
    current_page: 1,
    last_page: 1,
  });

  const projectUser = useSelector((state) => state.projectUser.projectUser);

  let hasCreatePermission = false;
  let hasEditPermission = false;
  let hasDeletePermission = false;

  if (projectUser) {
    if (projectUser?.role?.name === "Project Manager") {
      hasCreatePermission = true;
      hasEditPermission = true;
      hasDeletePermission = true;
    } else {
      hasCreatePermission = false;
      hasEditPermission = false;
      hasDeletePermission = false;
    }
  }

  const fetchSuccessStories = async () => {
    setIsLoading(true);
    try {
      const response = await getSuccessStories(projectId, rowsPerPage, page);
      setSuccessStories(response.data.data.data);
      setPaginationObject(response.data.data);
    } catch (error) {
      console.error("Error fetching success stories:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (projectId) {
      fetchSuccessStories();
    }
  }, [projectId, refresh, initialRefresh, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage + 1);
    fetchSuccessStories();
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleOpenDrawer = (story) => {
    setSelectedStory(story);
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setSelectedStory(null);
    setIsDrawerOpen(false);
  };

  const handleDeleteStory = async (storyId) => {
    setIsLoading(true);
    try {
      await deleteSuccessStory(storyId, projectId);
      setSuccessStories((prevStories) =>
        prevStories.filter((story) => story.id !== storyId)
      );
      toast.success("Success story deleted successfully");
    } catch (error) {
      console.error("Error deleting success story:", error);
      toast.error("Error deleting success story");
    } finally {
      setIsLoading(false);
    }
  };

  const handleStoryUpdate = (updatedStory) => {
    setSuccessStories((prevStories) =>
      prevStories.map((story) =>
        story.id === updatedStory.id ? updatedStory : story
      )
    );
  };

  return (
    <div className="successStoriesTab">
      <Container fluid>
        <div className="mainPageHeader d-flex justify-content-between mb-4">
          <BreadCrumb title={t("Success Stories")} />
          {/* enable only project status is draft or rejected state */}
          {!disabled && hasCreatePermission && (
            <Button
              variant="contained"
              className="popupButton edit_button main-login-btn btn btn-secondary d-flex align-items-center btn btn-secondary btn-height-30"
              disableElevation
              onClick={() => handleOpenDrawer(null)}
              startIcon={<AddOutlinedIcon />}
            >
              {t("Success Story")}
            </Button>
          )}
        </div>
        <Card className="viewProjectApproval" style={{ width: "100%" }}>
          <ul className="list-inline allComments historyTab height-scroll">
            {isLoading ? (
              <Loader />
            ) : successStories.length > 0 ? (
              successStories.map((story, index) => (
                <li className="flex justify-content-between py-3 mx-4" key={index}>
                  <div className="d-flex align-items-center">
                    <UserProfile
                      user={{ name: story.name }}
                      avatarClassName="me-2 ml-6"
                      profileClassName="me-2 ml-2"
                    />
                    <div>
                      <strong>{story.name}</strong>
                      <p>{story.description}</p>
                    </div>
                    <div
                      className="proSubmissionActions"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginLeft: "auto",
                      }}
                    >
                      {hasEditPermission && (
                        <ModeEditOutlineOutlinedIcon
                          sx={{ cursor: !disabled ? "pointer" : undefined }}
                          onClick={() => handleOpenDrawer(story)}
                        />
                      )}
                      {hasDeletePermission && (
                        <DeleteOutlineOutlinedIcon
                          sx={{ cursor: !disabled ? "pointer" : undefined }}
                          onClick={() => handleDeleteStory(story.id)}
                        />
                      )}
                    </div>
                  </div>
                </li>
              ))
            ) : (
              <NodataFound
                text="No success stories found"
                icon={MilestoneImage}
              />
            )}
          </ul>
        </Card>
        {successStories.length > 0 && (
          <div className="ongoingProjPagination d-flex justify-content-end mt-3 newRoundedPagination">
            <PaginationComponent
              paginationObject={paginationObject.meta}
              page={page - 1}
              handleChangePage={handleChangePage}
              rowsPerPage={rowsPerPage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
            />
          </div>
        )}
        <AddSuccessStoryDrawer
          projectId={projectId}
          isOpen={isDrawerOpen}
          toggle={handleCloseDrawer}
          selectedStory={selectedStory}
          onStoryUpdate={handleStoryUpdate}
          setRefresh={setRefresh}
        />
      </Container>
    </div>
  );
};

export default withTranslation()(SuccessStoriesTab);
